import ApiCaller from 'actions/ApiCaller';
import baseAjaxConfig from 'actions/BaseAjaxConfig';
import get from 'lodash/get';
import { isEmpty } from 'lodash';
import { getCartRefIds, EmptyCartErrorMessage } from './utils';
import { checkoutSummarySuccessAction } from './checkoutSummaryActions';
import { checkoutStateAdvanceAction } from './checkoutVisualStateActions';
import { newAddressFormKey, INITIAL_ADDRESS, GENERIC_ERROR, GENERIC_ERROR_MESSAGE } from 'constants/CheckoutConstants';

export const ADDRESS_DELIVER_SELECT_REQUEST = 'ADDRESS_DELIVER_SELECT_REQUEST';
export const ADDRESS_DELIVER_SELECT_SUCCESS = 'ADDRESS_DELIVER_SELECT_SUCCESS';
export const ADDRESS_DELIVER_SELECT_ERROR = 'ADDRESS_DELIVER_SELECT_ERROR';

export function selectDeliveryAddressRequestAction() {
	return { type: ADDRESS_DELIVER_SELECT_REQUEST };
}

export function selectDeliveryAddressSuccessAction() {
	return { type: ADDRESS_DELIVER_SELECT_SUCCESS };
}

export function selectDeliveryAddressErrorAction(error) {
	return { type: ADDRESS_DELIVER_SELECT_ERROR, error };
}

// TODO: Figure out a better text for this.
export const EmptyAddressErrorMessage = 'Address data is empty';

export const EmptyCartError = {
	errorCode: GENERIC_ERROR,
	GENERIC_ERROR: EmptyCartErrorMessage
};

export const EmptyAddressIdError = {
	errorCode: GENERIC_ERROR,
	GENERIC_ERROR: EmptyAddressErrorMessage
};

export function selectDeliveryAddressAction(addressId) {
	const settings = Object.assign({}, baseAjaxConfig, {
		pathname: `/api/5/checkout/contact/${addressId}/user_generated`
	});

	return (dispatch, getState) => {
		dispatch(selectDeliveryAddressRequestAction());
		const { checkoutPage } = getState();
		const cartItemRefIds = getCartRefIds(get(checkoutPage, ['summary', 'orderSummary']));

		// TODO: Is this the best way to error check?
		if (!addressId) {
			dispatch(selectDeliveryAddressErrorAction(EmptyAddressIdError));
			return Promise.reject(EmptyAddressIdError);
		}

		if (!cartItemRefIds.length) {
			dispatch(selectDeliveryAddressErrorAction(EmptyCartError));
			return Promise.reject(EmptyCartError);
		}

		// need to fetch cart refs from checkout init state
		const body = {
			cartItemRefIds,
			billingContactId: addressId
		};

		return ApiCaller.put(settings, body).then(
			json => {
				const response = get(json, ['RESPONSE']);
				// TODO: Remove address success actions, use a updateCheckoutSummary
				// action instead
				dispatch(selectDeliveryAddressSuccessAction());
				return dispatch(checkoutSummarySuccessAction(response));
			},
			err => {
				dispatch(selectDeliveryAddressErrorAction(err));
				return Promise.reject(err);
			}
		);
	};
}

export const CACHE_ADDRESS = 'CACHE_ADDRESS';
export const CLEAR_ADDRESS_CACHE = 'CLEAR_ADDRESS_CACHE';
export const CLEAR_ADDRESS_ERROR = 'CLEAR_ADDRESS_ERROR';
export function cacheAddressAction(address) {
	return {
		address,
		type: CACHE_ADDRESS
	};
}
// update the address. Useful in address correction flows in Checkout Address
export function cacheAddressClearErrorAction(address) {
	return dispatch => {
		dispatch(cacheAddressAction(address));

		return Promise.resolve(
			dispatch({
				address,
				type: CLEAR_ADDRESS_ERROR
			})
		);
	};
}

export function clearAddressCacheAction(addressId) {
	return {
		type: CLEAR_ADDRESS_CACHE,
		addressId
	};
}

export const UPSERT_ADDRESS_REQUEST = 'UPSERT_ADDRESS_REQUEST';
export const UPSERT_ADDRESS_SUCCESS = 'UPSERT_ADDRESS_SUCCESS';
export const UPSERT_ADDRESS_ERROR = 'UPSERT_ADDRESS_ERROR';

export const upsertAddressRequestAction = address => {
	return {
		type: UPSERT_ADDRESS_REQUEST,
		address
	};
};

export const upsertAddressErrorAction = (error, addressId, addressResponse) => {
	return {
		type: UPSERT_ADDRESS_ERROR,
		addressId,
		error,
		addressResponse
	};
};

export const cleanupAddress = (address = {}) => {
	if (!address || isEmpty(address) || typeof address !== 'object') {
		return {};
	}

	const cleanedAddress = {};

	// Pick the keys that are only valid address fields. Things like `error`,
	// `id` are removed. Pick only those key, value pairs that have some content
	// in them.
	Object.keys(INITIAL_ADDRESS).forEach(key => {
		const value = address[key];

		if (key !== 'id' && value) {
			Object.assign(cleanedAddress, { [key]: value });
		}
	});

	return cleanedAddress;
};

export const upsertAddressAction = address => {
	const settings = Object.assign({}, baseAjaxConfig, {
		pathname: '/api/5/checkout/contact',
		acceptErrors: true
	});
	const addressId = address.id;

	return (dispatch, getState) => {
		dispatch(upsertAddressRequestAction(address));

		const { checkoutPage } = getState();
		const cartItemRefIds = getCartRefIds(get(checkoutPage, ['summary', 'orderSummary']));

		if (!cartItemRefIds.length) {
			return Promise.reject(dispatch(upsertAddressErrorAction(EmptyCartError, addressId)));
		}

		// Pick only those fields we need, filter out fields that don't have any
		// data set in them, so that backend won't break UI when they happen to
		// change some restriction on some field.
		const cleanedAddress = cleanupAddress(address);
		const body = { cartItemRefIds, ...cleanedAddress };

		// Add the contactId field only if the address is being edited. If it's a
		// new address, sending the contactId doesn't make sense because
		// newAddressFormKey is something we use in the UI to differentiate.
		if (addressId !== newAddressFormKey) {
			Object.assign(body, { contactId: addressId });
		}

		const apiCaller = addressId === newAddressFormKey ? ApiCaller.post(settings, body) : ApiCaller.put(settings, body);

		return apiCaller.then(
			json => {
				const response = get(json, ['RESPONSE']);
				dispatch(clearAddressCacheAction(addressId));
				dispatch(checkoutSummarySuccessAction(response));
				return dispatch(checkoutStateAdvanceAction());
			},
			err => {
				const errorCode = get(err, ['RESPONSE', 'errorCode']);
				// TODO: This nasty hack is used because MAPI returns the keys as the keys
				// of the following object. And we need to convert them to our address key
				// representation. Get this fixed at MAPI end. They should follow a
				// consistent pattern.
				const errorMap = {
					USER_COMMUNICATION_INVALID: 'phone',
					ADD_PINCODE_INVALID: 'pincode',
					ADD_STATE_INVALID: 'state',
					ADD_LINE1_INVALID: 'addressLine1'
				};

				const normalisedErrorCode = errorMap[errorCode] || GENERIC_ERROR;
				const errorMessage =
					get(err, 'RESPONSE.addressResponse.message') ||
					get(err, ['RESPONSE', 'errorMessage']) ||
					GENERIC_ERROR_MESSAGE;

				// The errorCode and the key for error message are the same, and this
				// is a bit redundant, but we need to cater to both the generic error
				// message, and the per-field error message. For now this is the only
				// way to do it cleanly.
				const error = {
					[normalisedErrorCode]: errorMessage,
					errorCode: normalisedErrorCode
				};
				const addressResponse = get(err, 'RESPONSE.addressResponse', {});
				return dispatch(upsertAddressErrorAction(error, addressId, addressResponse));
			}
		);
	};
};
